import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, debounceTime, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { AumicoCoreProvider, NotificationType } from '../../../../aumico-lib-core-fe/src/lib';
import { PersonProjectsActions } from '../actions/person-projects.actions';
import { PersonProjectsService } from '../services/person-projects.service';

/** Backend key of `POST /person-projects` for an inactive person. */
const PERSON_INACTIVE_ERROR = 'aumico_backend_errors_person_inactive';

/** REV-22155 — person projects. */
@Injectable()
export class PersonProjectsEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly personProjectsService: PersonProjectsService,
        private readonly coreProvider: AumicoCoreProvider,
    ) {}

    loadPersonProjects$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PersonProjectsActions.loadPersonProjects),
            debounceTime(100),
            switchMap(({ filter }) =>
                this.personProjectsService.getPersonProjects(filter).pipe(
                    map((response) => PersonProjectsActions.personProjectsLoaded({ response })),
                    catchError((error: HttpErrorResponse) => of(PersonProjectsActions.personProjectsLoadFailure({ error }))),
                ),
            ),
        ),
    );

    loadPersonProject$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PersonProjectsActions.loadPersonProject),
            switchMap(({ projectId }) =>
                this.personProjectsService.getPersonProject(projectId).pipe(
                    map((project) => PersonProjectsActions.personProjectLoaded({ project })),
                    catchError((error: HttpErrorResponse) => of(PersonProjectsActions.personProjectLoadFailure({ error }))),
                ),
            ),
        ),
    );

    createPersonProject$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PersonProjectsActions.createPersonProject),
            mergeMap(({ personId, year }) =>
                this.personProjectsService.createPersonProject(personId, year).pipe(
                    map((project) => PersonProjectsActions.personProjectCreated({ project })),
                    catchError((error: HttpErrorResponse) => of(PersonProjectsActions.personProjectCreateFailure({ error }))),
                ),
            ),
        ),
    );

    notifyCreateFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(PersonProjectsActions.personProjectCreateFailure),
                tap(({ error }) =>
                    this.coreProvider.dispatchAddNotification(
                        'aumico_person_projects_create_failure_title',
                        JSON.stringify(error?.error ?? '').includes(PERSON_INACTIVE_ERROR)
                            ? 'aumico_person_projects_create_failure_inactive'
                            : 'aumico_person_projects_create_failure_description',
                        NotificationType.ERROR,
                    ),
                ),
            ),
        { dispatch: false },
    );

    updatePersonProjectStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PersonProjectsActions.updatePersonProjectStatus),
            mergeMap(({ projectId, status, updateTasks, mandateManagementTaskIds }) =>
                this.personProjectsService
                    .updatePersonProjectStatus(projectId, status, updateTasks, mandateManagementTaskIds)
                    .pipe(
                        map((project) => PersonProjectsActions.personProjectStatusUpdated({ project })),
                        catchError((error: HttpErrorResponse) =>
                            of(PersonProjectsActions.personProjectStatusUpdateFailure({ error })),
                        ),
                    ),
            ),
        ),
    );

    bulkUpdatePersonProjectStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PersonProjectsActions.bulkUpdatePersonProjectStatus),
            mergeMap(({ projectIds, status, updateTasks, mandateManagementTaskIds }) =>
                this.personProjectsService
                    .bulkUpdatePersonProjectStatus(projectIds, status, updateTasks, mandateManagementTaskIds)
                    .pipe(
                        tap(() =>
                            this.coreProvider.dispatchAddNotification(
                                'aumico_cpmm_project_status_bulk_update_success_title',
                                'aumico_cpmm_project_status_bulk_update_success_description',
                                NotificationType.SUCCESS,
                            ),
                        ),
                        map((projects) => PersonProjectsActions.bulkPersonProjectStatusUpdated({ projects })),
                        catchError((error: HttpErrorResponse) => {
                            this.coreProvider.dispatchAddNotification(
                                'aumico_cpmm_project_status_bulk_update_failure_title',
                                'aumico_cpmm_project_status_bulk_update_failure_description',
                                NotificationType.ERROR,
                            );
                            return of(PersonProjectsActions.bulkUpdatePersonProjectStatusFailure({ error }));
                        }),
                    ),
            ),
        ),
    );

    deletePersonProject$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PersonProjectsActions.deletePersonProject),
            mergeMap(({ projectId }) =>
                this.personProjectsService.deletePersonProject(projectId).pipe(
                    map(() => PersonProjectsActions.personProjectDeleted({ projectId })),
                    catchError((error: HttpErrorResponse) => of(PersonProjectsActions.personProjectDeleteFailure({ error }))),
                ),
            ),
        ),
    );

    bulkDeletePersonProjects$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PersonProjectsActions.bulkDeletePersonProjects),
            mergeMap(({ projectIds }) =>
                this.personProjectsService.bulkDeletePersonProjects(projectIds).pipe(
                    tap(() =>
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_projects_bulk_delete_success_title',
                            'aumico_cpmm_projects_bulk_delete_success_description',
                            NotificationType.SUCCESS,
                        ),
                    ),
                    map(({ deletedIds }) => PersonProjectsActions.bulkPersonProjectsDeleted({ deletedIds })),
                    catchError((error: HttpErrorResponse) =>
                        of(PersonProjectsActions.bulkDeletePersonProjectsFailure({ error })),
                    ),
                ),
            ),
        ),
    );
}
